import { zodResolver } from '@hookform/resolvers/zod';
import Save from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FloatingSaveButton } from '@/components/floating-save-button';

import AuthGuard from '@/components/auth-guard';
// Form Sections
import { BasicIdentitySection } from '@/components/event-form/basic-identity';
import { CustomFieldsSection } from '@/components/event-form/custom-fields-section';
import { RelationshipsSection } from '@/components/event-form/relationships';
import type { EventEntityChangeWithFailure } from '@/components/event-implications/event-change-card';
import { EventImplicationsSection } from '@/components/event-implications/event-implications-section';
import { Page } from '@/components/page';
import {
  EventFormSchema,
  EventFormValues,
  useEventAccessLayer
} from '@/features/events';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { FieldChange, PrimaryEntityType } from '@/lib/types';
import type { ReciprocalDeltaPayload } from '@/lib/utils/generate-reciprocal-deltas';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';
import type { EventSummary } from '@common/schemas/entities/summary.schema';

type PendingImplication = {
  entityId: string;
  entityType: PrimaryEntityType;
  entityName: string;
  changes: FieldChange[];
  reciprocalChanges: ReciprocalDeltaPayload[];
  description: string;
};

export const Route = createFileRoute('/events/new')({
  component: NewEventPage,
});

function NewEventForm() {
  const navigate = useNavigate();
  const search = Route.useSearch() as { order?: string };
  const {
    createEvent: createEventLayer,
    sections: eventSettings,
    getNewId,
    events,
  } = useEventAccessLayer();

  const { novel, batchSaveItemsNonBlocking, updateAllEventSummaries } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const { backend } = useFirebase();
  const [isSaving, setIsSaving] = useState(false);
  const [eventId] = useState(() => getNewId());
  const [pendingImplications, setPendingImplications] = useState<PendingImplication[]>([]);
  const { t } = useTranslation();

  if (!novel.id) {
    navigate({ to: '/' });
    return null;
  }

  const orderFromUrl = useMemo(() => {
    return search.order ? parseInt(search.order, 10) : 0;
  }, [search]);

  const methods = useForm<EventFormValues>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: {
      name: '',
      type: '',
      date: '',
      shortDescription: '',
      whyItMatters: '',
      order: orderFromUrl,
      characterIds: [],
      organizationIds: [],
      locationIds: [],
      isNarrativelyComplete: false,
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    },
  });

  const onSubmit = async (values: EventFormValues) => {

    setIsSaving(true);
    try {
      const { defined: coreData, details } = splitBySchemaShape(EventFormSchema, values);

      // We NEVER want the main form save to overwrite `eventChanges` which are managed independently.
      delete details.eventChanges;
      delete (coreData as Record<string, unknown>).eventChanges;

      await createEventLayer(eventId, {
        ...coreData,
        id: eventId,
        details,
      } as EventFormValues);

      // Shift subsequent events to make room for the new one if we are inserting
      const insertOrder = values.order || 0;
      const currentEvents: EventSummary[] = events || [];
      const eventsToShift = currentEvents.filter(e => (e.order ?? 0) >= insertOrder);

      if (eventsToShift.length > 0) {
        const updatedEvents = eventsToShift.map(e => ({
          id: e.id,
          order: (e.order ?? 0) + 1,
        }));

        batchSaveItemsNonBlocking('entities', 'events', updatedEvents);

        const summaries: Record<string, EventSummary> = {};
        eventsToShift.forEach(e => {
          summaries[e.id] = { ...e, order: (e.order ?? 0) + 1 };
        });
        updateAllEventSummaries(summaries);
      }

      let hasFailures = false;
      const failedImplications: (PendingImplication & { failedToSave?: boolean })[] = [];

      if (pendingImplications.length > 0) {
        for (const imp of pendingImplications) {
          try {
            // Save direct implication
            await backend.entities.saveEventImplication(
              novel.ownerId,
              novel,
              eventId,
              values.name || '',
              values.order || 0,
              imp.entityType,
              imp.entityId,
              imp.entityId, // fallback name
              imp.changes,
              imp.description,
              false
            );

            // Save reciprocals
            for (const rec of imp.reciprocalChanges) {
              await backend.entities.saveEventImplication(
                novel.ownerId,
                novel,
                eventId,
                values.name || '',
                values.order || 0,
                rec.entityType,
                rec.entityId,
                rec.entityName || rec.entityId,
                rec.changes,
                t('event_implications.reciprocal_change'),
                false
              );
            }
          } catch (error) {
            hasFailures = true;
            failedImplications.push({
              ...imp,
              failedToSave: true
            });
            console.error('Failed to save implication:', error);
          }
        }
      }

      if (hasFailures) {
        toast({
          title: t('entity_form.events.create_success_title'),
          description: 'Event created, but some changes failed to save.',
          variant: 'destructive',
        });
        sessionStorage.setItem(`failedImplications_${eventId}`, JSON.stringify(failedImplications));
        navigate({ to: `/events/edit/${eventId}` });
      } else {
        toast({
          title: t('entity_form.events.create_success_title'),
          description: t('entity_form.events.create_success_desc', { name: values.name }),
        });
        navigate({ to: '/events' });
      }

    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: t('entity_form.common.save_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onError = (errors: FieldErrors<EventFormValues>) => {
    console.error('Form validation errors:', errors);
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey && errors[firstErrorKey as keyof typeof errors]?.message) {
      toast({
        title: t('entity_form.common.validation_failed_title'),
        description: (errors[firstErrorKey as keyof typeof errors]?.message as string) || t('entity_form.events.validation_failed_fallback_desc'),
        variant: 'destructive',
      });
    } else {
      toast({
        title: t('entity_form.common.validation_failed_title'),
        description: t('entity_form.common.validation_failed_generic_desc'),
        variant: 'destructive',
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
        <Page
          title={t('entity_form.events.create_title')}
          description={t('entity_form.events.create_desc')}
          breadcrumbs={[
            { label: t('entity_list.events.title'), href: '/events' },
            { label: t('entity_form.events.breadcrumb_new') }
          ]}
          layoutMode="scrolling"
          actions={
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {t('entity_form.events.save')}
            </Button>
          }
        >

          <Stack spacing={4}>
            {novelId && <BasicIdentitySection />}
            {novelId && <RelationshipsSection />}
            {novelId && <CustomFieldsSection novelId={novelId} sections={eventSettings} />}
            {novelId && (
              <EventImplicationsSection
                eventId={eventId}
                eventData={{
                  name: methods.watch('name'),
                  order: methods.watch('order'),
                  characterIds: methods.watch('characterIds'),
                  organizationIds: methods.watch('organizationIds'),
                  locationIds: methods.watch('locationIds'),
                  eventChanges: pendingImplications.map(imp => ({
                    entityType: imp.entityType,
                    entityId: imp.entityId,
                    entityName: imp.entityName,
                    changes: imp.changes,
                    description: imp.description,
                  })) as EventEntityChangeWithFailure[],
                }}
                onSaveOverride={async (entityId, entityType, entityName, changes, reciprocalChanges, description) => {
                  for (const rec of reciprocalChanges) {
                    if (rec.entityType === 'characters') {
                      const chars = methods.getValues('characterIds') || [];
                      if (!chars.includes(rec.entityId)) {
                        methods.setValue('characterIds', [...chars, rec.entityId], { shouldDirty: true });
                      }
                    } else if (rec.entityType === 'organizations') {
                      const orgs = methods.getValues('organizationIds') || [];
                      if (!orgs.includes(rec.entityId)) {
                        methods.setValue('organizationIds', [...orgs, rec.entityId], { shouldDirty: true });
                      }
                    } else if (rec.entityType === 'locations') {
                      const locs = methods.getValues('locationIds') || [];
                      if (!locs.includes(rec.entityId)) {
                        methods.setValue('locationIds', [...locs, rec.entityId], { shouldDirty: true });
                      }
                    }
                  }

                  setPendingImplications(prev => {
                    const nextMap = new Map(prev.map(p => [p.entityId, p]));
                    nextMap.set(entityId, { entityId, entityType, entityName, changes, reciprocalChanges, description });
                    for (const rec of reciprocalChanges) {
                      nextMap.set(rec.entityId, {
                        entityId: rec.entityId,
                        entityType: rec.entityType,
                        entityName: rec.entityName || rec.entityId,
                        changes: rec.changes,
                        reciprocalChanges: [],
                        description: t('event_implications.reciprocal_change'),
                      });
                    }
                    return Array.from(nextMap.values());
                  });
                }}
              />
            )}
          </Stack>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isSaving}
          label={t('entity_form.events.save')}
        />
      </form>
    </FormProvider>
  );
}

export default function NewEventPage() {
  return (
    <AuthGuard>
      <Suspense fallback={<CircularProgress />}>
        <NewEventForm />
      </Suspense>
    </AuthGuard>
  )
}
